import React, { useEffect, useState, useContext, useRef } from "react";
import {
  Table,
  Spinner,
  Alert,
  Container,
  Card,
  Button,
  Badge,
  Form,
} from "react-bootstrap";
import { useParams, useNavigate } from "react-router-dom";
import { ThemeContext } from "contexts/ThemeContext.jsx";
import { serverHost } from "variables/serverdata";
import HusariaHeader from "views/pages/components/HusariaHeader";

const GreetingDetails = () => {
  const { theme } = useContext(ThemeContext);
  const isDark = theme === "dark";
  const { id } = useParams();
  const audioRef = useRef(null);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  const [greetings, setGreetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentlyPlaying, setCurrentlyPlaying] = useState(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [uploadingId, setUploadingId] = useState(null);

  const fetchGreetings = async () => {
    try {
      setLoading(true);

      const response = await fetch(`${serverHost}/voicemails/${id}`);
      if (!response.ok) throw new Error("Failed to fetch greetings.");

      const data = await response.json();
      const results = data.greetings || [];
      console.log(results);
      setGreetings(results);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGreetings();
  }, [id]);

  const formatFileSize = (bytes) => {
    if (!bytes) return "-";
    return `${(bytes / 1024).toFixed(2)} KB`;
  };

  const formatDuration = (seconds) => {
    if (!seconds) return "-";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const handlePlay = (path, row) => {
    if (currentlyPlaying === row["filename"]) {
          audioRef.current.pause();
          audioRef.current.currentTime = 0;
          setCurrentlyPlaying(null);
          return;
        }
    
        audioRef.current.src = `${serverHost}/play_voicemail/${encodeURIComponent(
          path
        )}`;
        audioRef.current.play();
        setCurrentlyPlaying(row["filename"]);
  };

  const handleUpload = (greetingId) => {
    setUploadingId(greetingId);
    fileInputRef.current.click();
  };

  const handleFileSelected = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);
    formData.append("greeting_id", uploadingId);

    try {
      const response = await fetch(`${serverHost}/upload_greeting`, {
        method: "POST",
        body: formData,
      });

      if (!response.ok) throw new Error("Failed to upload greeting.");

      await fetchGreetings();
      setUploadingId(null);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDelete = async (greetingId) => {
    if (!window.confirm("Are you sure you want to delete this greeting?")) {
      return;
    }

    try {
      const response = await fetch(`${serverHost}/greetings/${greetingId}`, {
        method: "DELETE",
      });

      if (!response.ok) throw new Error("Failed to delete greeting.");

      await fetchGreetings();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleSelectedChange = async (greetingId, isSelected) => {
    try {
      const response = await fetch(
        `${serverHost}/update_selected_greeting/${greetingId}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ selected: isSelected }),
        },
      );

      if (!response.ok) throw new Error("Failed to update selection.");

      await fetchGreetings();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDownload = async (msg) => {
    console.log(msg);
    const url = `${serverHost}/download/${msg.full_path}`;
    await fetch(url)
      .then((resp) => resp.blob())
      .then((blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "greeting.wav";
        a.click();
      });
  };

  const handleAudioEnded = () => {
    setCurrentlyPlaying(null);
  };

  const goToSettings = () => {
    navigate(`/admin/hucs/edit/voicemail/${id}`);
  };

  const handleBack = () => {
    navigate(`/admin/hucs/voicemails`);
  };

  if (loading)
    return (
      <div className="text-center mt-5">
        <Spinner animation="border" variant={isDark ? "light" : "primary"} />
        <p className="mt-2" style={{ color: isDark ? "#e2e8f0" : "#212529" }}>
          Loading greetings...
        </p>
      </div>
    );

  if (error)
    return (
      <Alert variant="danger" className="mt-5 text-center">
        {error}
      </Alert>
    );

  return (
    <>
      <HusariaHeader
        name="Greeting Messages"
        parentName="HUCS"
        padding="4.5rem"
      />

      <Container fluid className="mt--6">
        <audio
          ref={audioRef}
          onEnded={handleAudioEnded}
          onTimeUpdate={() => {
            if (audioRef.current) {
              setCurrentTime(audioRef.current.currentTime);
            }
          }}
          onLoadedMetadata={() => {
            if (audioRef.current) {
              setDuration(audioRef.current.duration);
            }
          }}
        />
        <input
          type="file"
          ref={fileInputRef}
          style={{ display: "none" }}
          accept="audio/*"
          onChange={handleFileSelected}
        />
        {currentlyPlaying && (
          <Card
            className={`mb-3 ${isDark ? "bg-dark text-light" : ""}`}
            style={{ borderLeft: "4px solid #0d6efd" }}
          >
            <Card.Body className="py-2">
              <div className="d-flex justify-content-between align-items-center mb-1">
                <strong>Now Playing:</strong>
                <span>
                  {greetings.find((g) => g.id === currentlyPlaying)?.name ||
                    currentlyPlaying}
                </span>
              </div>

              <div
                style={{
                  height: "8px",
                  backgroundColor: isDark ? "#374151" : "#e5e7eb",
                  borderRadius: "4px",
                  overflow: "hidden",
                  cursor: "pointer",
                }}
                onClick={(e) => {
                  const rect = e.currentTarget.getBoundingClientRect();
                  const percent = (e.clientX - rect.left) / rect.width;
                  audioRef.current.currentTime = percent * duration;
                }}
              >
                <div
                  style={{
                    width: `${duration ? (currentTime / duration) * 100 : 0}%`,
                    height: "100%",
                    backgroundColor: "#0d6efd",
                    transition: "width 0.1s linear",
                  }}
                />
              </div>

              <div
                className="d-flex justify-content-between mt-1"
                style={{ fontSize: "0.8rem" }}
              >
                <span>{formatDuration(currentTime)}</span>
                <span>{formatDuration(duration)}</span>
              </div>
            </Card.Body>
          </Card>
        )}
        <Card
          className={`shadow-sm mb-4 ${isDark ? "bg-dark text-light" : ""}`}
        >
          <Card.Header
            style={{
              backgroundColor: isDark ? "#1f2937" : "#f0f0f0",
              color: isDark ? "#e2e8f0" : "#111",
            }}
            className="d-flex justify-content-between align-items-center"
          >
            <div className="d-flex align-items-center gap-3">
              <Badge bg="info" style={{ marginLeft: "0.5rem" }}>
                {greetings.length} greetings
              </Badge>
            </div>

            <div className="d-flex gap-2">
              <Button variant="info" size="sm" onClick={fetchGreetings}>
                <i class="fas fa-sync-alt"></i> Refresh
              </Button>
              <Button variant="primary" size="sm" onClick={goToSettings}>
                <i class="fas fa-upload"></i> Upload
              </Button>
              <Button variant="light" size="sm" onClick={goToSettings}>
                <i class="fas fa-user-cog"></i> Settings
              </Button>
            </div>
          </Card.Header>

          <Card.Body style={{ padding: 0 }}>
            <div style={{ overflowX: "auto" }}>
              <Table
                striped
                hover
                bordered
                responsive
                className="mb-0"
                style={{
                  minWidth: "1000px",
                  backgroundColor: isDark ? "#1f2937" : "#fff",
                  color: isDark ? "#e2e8f0" : "#212529",
                }}
              >
                <thead>
                  <tr>
                    <th className="text-center">Selected</th>
                    <th className="text-center">#</th>
                    <th className="text-center">Name</th>
                    <th className="text-center">Filename</th>
                    <th className="text-center">Size</th>
                    <th className="text-center">Uploaded</th>
                    <th className="text-center">Description</th>
                    <th className="text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {greetings.map((greeting) => {
                    const isPlaying = currentlyPlaying === greeting.filename;

                    return (
                      <tr key={greeting.id}>
                        <td className="text-center">
                          <Form.Check
                            type="radio"
                            name="selectedGreeting"
                            checked={greeting.selected || false}
                            onChange={(e) =>
                              handleSelectedChange(
                                greeting.id,
                                e.target.checked,
                              )
                            }
                          />
                        </td>
                        <td className="text-center">{greeting.greeting_id}</td>
                        <td className="text-center">{greeting.name || "-"}</td>
                        <td className="text-center">
                          {greeting.filename || "-"}
                        </td>
                        <td className="text-center">
                          {formatFileSize(greeting.file_size)}
                        </td>
                        <td className="text-center">
                          {greeting.uploaded_date
                            ? new Date(greeting.uploaded_date).toLocaleString(
                                "en-US",
                                {
                                  timeZone: "America/Los_Angeles",
                                },
                              )
                            : "-"}
                        </td>
                        <td className="text-center">
                          {greeting.description || "-"}
                        </td>

                        <td className="text-center">
                          <Button
                            variant={isPlaying ? "warning" : "success"}
                            size="sm"
                            onClick={() =>
                              handlePlay(greeting.full_path, greeting)
                            }
                            disabled={!greeting.filename}
                          >
                            {isPlaying ? "⏸ Stop" : "▶ Play"}
                          </Button>
                          <Button
                            variant="primary"
                            size="sm"
                            onClick={() => handleDownload(greeting)}
                          >
                            <i class="fas fa-download"></i> Download
                          </Button>
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() => handleDelete(greeting.id)}
                            disabled={!greeting.filename}
                          >
                            <i class="far fa-trash-alt"></i> Delete
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
              {greetings.length === 0 && (
                <Alert
                  variant="info"
                  className="text-center"
                  style={{ marginTop: "1rem", color: "black" }}
                >
                  No greetings found.
                </Alert>
              )}
            </div>
          </Card.Body>
        </Card>
        <Button variant="light" onClick={handleBack}>
          Back
        </Button>
      </Container>
    </>
  );
};

export default GreetingDetails;
